import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { isAdminImpersonatingUser, revertToAdmin } from '../../utils/adminLoginUtils';
import { auth } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Component to display an overlay when an admin is impersonating a user
 */
const AdminImpersonationOverlay: React.FC = () => {
  // Check if admin is impersonating a user
  const isImpersonating = isAdminImpersonatingUser();
  const [userEmail, setUserEmail] = useState<string>('loading...');
  
  // Listen for auth state changes to get the current user email
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email || 'unknown user');
      } else {
        setUserEmail('not logged in');
      }
    });
    
    // Clean up the listener on unmount
    return () => unsubscribe();
  }, []);

  if (!isImpersonating) {
    return null;
  }

  const handleRevertToAdmin = async () => {
    await revertToAdmin();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 0, 0, 0.1)',
        padding: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 9999,
        borderTop: '1px solid #ff0000'
      }}
    >
      <Typography variant="body2" color="error">
        <strong>Admin Mode:</strong> You are viewing as user: {userEmail}
      </Typography>
      <Button 
        variant="contained" 
        color="error" 
        size="small" 
        onClick={handleRevertToAdmin}
      >
        Return to Admin
      </Button>
    </Box>
  );
};

export default AdminImpersonationOverlay;
